import React, { useState } from 'react';
import { Box, Typography, Paper, TextField, MenuItem, Button, Alert } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Search as SearchIcon } from '@mui/icons-material';
import { DisponibilidadBancariaService } from '../services/disponibilidadService';
import type { DisponibilidadBancariaResponse } from '../services/disponibilidadService';

const MESES = [
  { value: '01', label: 'Enero' },
  { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },
  { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

const DisponibilidadBancariaPage: React.FC = () => {
  const [periodoInicio, setPeriodoInicio] = useState('01');
  const [periodoFin, setPeriodoFin] = useState('12');
  const [ejercicio, setEjercicio] = useState(new Date().getFullYear().toString());
  const [data, setData] = useState<DisponibilidadBancariaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConsultar = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await DisponibilidadBancariaService.getDisponibilidad(
        periodoInicio,
        periodoFin,
        ejercicio,
      );
      setData(result);
    } catch {
      setError('Error al consultar disponibilidad bancaria');
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'codigo', headerName: 'Codigo', width: 80 },
    { field: 'nombre', headerName: 'Caja/Banco', flex: 1, minWidth: 200 },
    { field: 'tipoCajaBanco', headerName: 'Tipo', width: 80 },
    {
      field: 'ingresosSoles',
      headerName: 'Ingresos S/',
      width: 140,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
    {
      field: 'egresosSoles',
      headerName: 'Egresos S/',
      width: 140,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
    {
      field: 'saldoSoles',
      headerName: 'Saldo S/',
      width: 140,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
    {
      field: 'ingresosDolares',
      headerName: 'Ingresos $',
      width: 140,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('en-US', { minimumFractionDigits: 2 }),
    },
    {
      field: 'egresosDolares',
      headerName: 'Egresos $',
      width: 140,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('en-US', { minimumFractionDigits: 2 }),
    },
    {
      field: 'saldoDolares',
      headerName: 'Saldo $',
      width: 140,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('en-US', { minimumFractionDigits: 2 }),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        Disponibilidad Bancaria
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Ejercicio"
            size="small"
            value={ejercicio}
            onChange={(e) => setEjercicio(e.target.value)}
            sx={{ width: 100 }}
          />
          <TextField
            label="Periodo Inicio"
            select
            size="small"
            value={periodoInicio}
            onChange={(e) => setPeriodoInicio(e.target.value)}
            sx={{ width: 150 }}
          >
            {MESES.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Periodo Fin"
            select
            size="small"
            value={periodoFin}
            onChange={(e) => setPeriodoFin(e.target.value)}
            sx={{ width: 150 }}
          >
            {MESES.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleConsultar}
            disabled={loading}
          >
            Consultar
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ height: 'calc(100vh - 300px)' }}>
        <DataGrid
          rows={data?.items || []}
          columns={columns}
          getRowId={(row) => row.idCajaBanco}
          loading={loading}
          pageSizeOptions={[25, 50]}
          disableRowSelectionOnClick
        />
      </Paper>

      {data?.totales && (
        <Paper sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2 }}>
            <Typography>
              <strong>Total Ingresos S/:</strong>{' '}
              {data.totales.totalIngresosSoles.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
            </Typography>
            <Typography>
              <strong>Total Egresos S/:</strong>{' '}
              {data.totales.totalEgresosSoles.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
            </Typography>
            <Typography>
              <strong>Total Saldo S/:</strong>{' '}
              {data.totales.totalSaldoSoles.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
            </Typography>
            <Typography>
              <strong>Total Ingresos $:</strong>{' '}
              {data.totales.totalIngresosDolares.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </Typography>
            <Typography>
              <strong>Total Egresos $:</strong>{' '}
              {data.totales.totalEgresosDolares.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </Typography>
            <Typography>
              <strong>Total Saldo $:</strong>{' '}
              {data.totales.totalSaldoDolares.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default DisponibilidadBancariaPage;
